import React from "react";
import { Star } from "lucide-react";
import { Button, ButtonGroup } from "@/components/ui";
import {
  ReviewFiltersPopoverLayout,
  ReviewSentimentFilterButtons,
  ReviewSourceFilterButtons,
  ReviewTriageFilterButtons,
  ReviewTimeFrameFilter,
  type ReviewDatePreset,
} from "@/components/reviews";
import { useReviewFilters } from "../ReviewFiltersContext";
import { format } from "date-fns";

interface ReviewFiltersPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReviewFiltersPopover({ onOpenChange }: ReviewFiltersPopoverProps) {
  const {
    sourceSystemFilter,
    setSourceSystemFilter,
    ratingTypeFilter,
    setRatingTypeFilter,
    preset,
    setPreset,
    date,
    setDate,
    triageFilter,
    setTriageFilter,
    sortFilter,
    setSortFilter,
    orderFilter,
    setOrderFilter,
    sentimentFilter,
    setSentimentFilter,
    clearFilters,
  } = useReviewFilters();

  const handleClear = () => {
    clearFilters();
  };

  const startDate = date?.from ? format(date.from, "yyyy-MM-dd") : null;
  const endDate = date?.to ? format(date.to, "yyyy-MM-dd") : null;

  const hasPopoverFilters = Boolean(
    triageFilter ||
      sourceSystemFilter ||
      ratingTypeFilter ||
      (preset && preset !== "all_time") ||
      sentimentFilter ||
      (sortFilter && sortFilter !== "effective_date") ||
      (orderFilter && orderFilter !== "desc") ||
      date?.from ||
      date?.to
  );

  return (
    <ReviewFiltersPopoverLayout
      onClear={hasPopoverFilters ? handleClear : undefined}
      onDone={() => onOpenChange(false)}
    >
      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sort Reviews</label>
        <ButtonGroup>
          <Button
            type="button"
            variant={sortFilter === "effective_date" && orderFilter === "desc" ? "default" : "outline"}
            size="xs"
            className="rounded-full font-semibold text-xs py-1 px-3.5 h-8 transition-all"
            onClick={() => {
              setSortFilter("effective_date");
              setOrderFilter("desc");
            }}
          >
            Newest
          </Button>
          <Button
            type="button"
            variant={sortFilter === "effective_date" && orderFilter === "asc" ? "default" : "outline"}
            size="xs"
            className="rounded-full font-semibold text-xs py-1 px-3.5 h-8 transition-all"
            onClick={() => {
              setSortFilter("effective_date");
              setOrderFilter("asc");
            }}
          >
            Oldest
          </Button>
          <Button
            type="button"
            variant={sortFilter === "star_rating" && orderFilter === "desc" ? "default" : "outline"}
            size="xs"
            className="rounded-full font-semibold text-xs py-1 px-3.5 h-8 transition-all flex items-center gap-1.5"
            onClick={() => {
              setSortFilter("star_rating");
              setOrderFilter("desc");
              setRatingTypeFilter("review");
            }}
          >
            Highest
            <Star className="size-3 text-current" fill="currentColor" />
          </Button>
          <Button
            type="button"
            variant={sortFilter === "star_rating" && orderFilter === "asc" ? "default" : "outline"}
            size="xs"
            className="rounded-full font-semibold text-xs py-1 px-3.5 h-8 transition-all flex items-center gap-1.5"
            onClick={() => {
              setSortFilter("star_rating");
              setOrderFilter("asc");
              setRatingTypeFilter("review");
            }}
          >
            Lowest
            <Star className="size-3 text-current" />
          </Button>
        </ButtonGroup>
      </div>

      <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Review Status</label>
        <ReviewTriageFilterButtons value={triageFilter} onChange={setTriageFilter} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Platform</label>
          <ReviewSourceFilterButtons value={sourceSystemFilter} onChange={setSourceSystemFilter} />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sentiment</label>
          <ReviewSentimentFilterButtons value={sentimentFilter} onChange={setSentimentFilter} />
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Source</label>
        <ButtonGroup>
          <Button
            type="button"
            variant={ratingTypeFilter === "review" ? "default" : "outline"}
            size="xs"
            className="rounded-full font-semibold text-xs py-1 px-3.5 h-8 transition-all"
            onClick={() => setRatingTypeFilter(ratingTypeFilter === "review" ? null : "review")}
          >
            Review
          </Button>
          <Button
            type="button"
            variant={ratingTypeFilter === "survey" ? "default" : "outline"}
            size="xs"
            className="rounded-full font-semibold text-xs py-1 px-3.5 h-8 transition-all"
            onClick={() => setRatingTypeFilter(ratingTypeFilter === "survey" ? null : "survey")}
          >
            Survey
          </Button>
        </ButtonGroup>
      </div>

      <div className="pt-2 border-t border-slate-100">
        <ReviewTimeFrameFilter
          preset={(preset as ReviewDatePreset | null) || null}
          startDate={startDate}
          endDate={endDate}
          onChange={({ preset: nextPreset, startDate: nextStart, endDate: nextEnd }) => {
            setPreset(nextPreset);
            setDate({
              from: nextStart ? new Date(`${nextStart}T00:00:00`) : undefined,
              to: nextEnd ? new Date(`${nextEnd}T23:59:59`) : undefined,
            });
          }}
        />
      </div>
    </ReviewFiltersPopoverLayout>
  );
}
